/* eslint-disable max-lines */
import {
  CanvasDimensions,
  createDefaultZone,
  ImageResolution,
  SpecialZoneType,
  ZonePolygonData,
} from '@hakimo-ui/hakimo/ui-elements';
import { MutableRefObject, useCallback } from 'react';
import {
  getInitialZone,
  isNewZone,
  removeFromInitialZones,
  removeZone,
  resetZoneToInitial,
  updateInitialZonesAfterSave,
} from './zoneHelpers';

interface UseZoneCRUDParams {
  zones: ZonePolygonData[];
  setZones: React.Dispatch<React.SetStateAction<ZonePolygonData[]>>;
  canvasDimensions: CanvasDimensions | null;
  imageResolution: ImageResolution | null;
  initialZonesRef: MutableRefObject<ZonePolygonData[]>;
  initialZoneIdsRef: MutableRefObject<Set<string>>;
  selectedZoneId: string | null;
  setSelectedZoneId: React.Dispatch<React.SetStateAction<string | null>>;
  saveZonesByType: (
    zones: ZonePolygonData[],
    type: SpecialZoneType,
    canvasDimensions: CanvasDimensions,
    imageResolution: ImageResolution,
    onSuccess?: () => void
  ) => void;
}

export function useZoneCRUD(params: UseZoneCRUDParams) {
  const {
    zones,
    setZones,
    canvasDimensions,
    imageResolution,
    initialZonesRef,
    initialZoneIdsRef,
    selectedZoneId,
    setSelectedZoneId,
    saveZonesByType,
  } = params;

  const handleAddZone = useCallback(
    (type: SpecialZoneType) => {
      if (!canvasDimensions) return;
      const newZone = createDefaultZone(canvasDimensions, type);

      setZones((prev) => {
        let updated = prev;
        if (selectedZoneId) {
          if (isNewZone(selectedZoneId, initialZoneIdsRef)) {
            updated = removeZone(updated, selectedZoneId);
          } else {
            const initial = getInitialZone(selectedZoneId, initialZonesRef);
            if (initial)
              updated = resetZoneToInitial(updated, selectedZoneId, initial);
          }
        }
        return [...updated, newZone];
      });
      setSelectedZoneId(newZone.id);
    },
    [
      canvasDimensions,
      selectedZoneId,
      setZones,
      setSelectedZoneId,
      initialZonesRef,
      initialZoneIdsRef,
    ]
  );

  const handleResetZone = useCallback(() => {
    if (!selectedZoneId) return;
    if (isNewZone(selectedZoneId, initialZoneIdsRef)) {
      setZones((prev) => removeZone(prev, selectedZoneId));
      setSelectedZoneId(null);
      return;
    }
    const initial = getInitialZone(selectedZoneId, initialZonesRef);
    if (initial)
      setZones((prev) => resetZoneToInitial(prev, selectedZoneId, initial));
  }, [
    selectedZoneId,
    setZones,
    setSelectedZoneId,
    initialZonesRef,
    initialZoneIdsRef,
  ]);

  const handleSaveZone = useCallback(() => {
    if (!selectedZoneId || !canvasDimensions || !imageResolution) return;
    const selectedZone = zones.find((z) => z.id === selectedZoneId);
    if (!selectedZone) return;

    const onSuccess = () => {
      updateInitialZonesAfterSave(
        initialZonesRef,
        initialZoneIdsRef,
        selectedZone,
        isNewZone(selectedZoneId, initialZoneIdsRef)
      );
      setSelectedZoneId(null);
    };

    saveZonesByType(
      zones,
      selectedZone.type,
      canvasDimensions,
      imageResolution,
      onSuccess
    );
  }, [
    selectedZoneId,
    zones,
    canvasDimensions,
    imageResolution,
    saveZonesByType,
    setSelectedZoneId,
    initialZonesRef,
    initialZoneIdsRef,
  ]);

  const handleDeleteZone = useCallback(
    (onComplete?: () => void) => {
      if (!selectedZoneId || !canvasDimensions || !imageResolution) return;
      const selectedZone = zones.find((z) => z.id === selectedZoneId);
      if (!selectedZone) return;

      const updatedZones = removeZone(zones, selectedZoneId);
      setZones(updatedZones);

      if (isNewZone(selectedZoneId, initialZoneIdsRef)) {
        setSelectedZoneId(null);
        onComplete?.();
        return;
      }

      const onSuccess = () => {
        removeFromInitialZones(
          initialZonesRef,
          initialZoneIdsRef,
          selectedZoneId
        );
        setSelectedZoneId(null);
        onComplete?.();
      };

      saveZonesByType(
        updatedZones,
        selectedZone.type,
        canvasDimensions,
        imageResolution,
        onSuccess
      );
    },
    [
      selectedZoneId,
      zones,
      canvasDimensions,
      imageResolution,
      setZones,
      setSelectedZoneId,
      saveZonesByType,
      initialZonesRef,
      initialZoneIdsRef,
    ]
  );

  return { handleAddZone, handleResetZone, handleSaveZone, handleDeleteZone };
}
